// Privacy & Consent center — self-service for ANY authenticated user (employees
// included). Granular, purpose-based consent the user can grant or withdraw at any
// time; each change is recorded immutably server-side (compliance-service) and
// emits a consent event other services honour (e.g. notifications + `marketing`).
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, Loader2, Lock, Check, FileText, Download, Pencil, Trash2, Ban, Send,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  getConsents, updateConsents, getMyPrivacyRequests, submitPrivacyRequest, downloadMyExport,
  type ConsentState, type PrivacyRequest,
  getDataSharingGrants,
  withdrawDataSharingGrant,
  getMyDataAccess,
  type DataSharingGrant,
  type DataAccessEntry,
} from '@/services/complianceApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { creditApi } from '@/features/credit/api/creditApi';

const EXPORTABLE = new Set(['access', 'export', 'portability']);

const fmtWhen = (s?: string) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;

// Data-subject rights the user can exercise (maps to compliance request_type).
const DATA_RIGHTS: { type: string; label: string; desc: string; icon: React.ReactNode }[] = [
  { type: 'access', label: 'Get a copy of my data', desc: 'See the personal data held about you.', icon: <FileText className="h-4 w-4" /> },
  { type: 'export', label: 'Export my data', desc: 'Receive your data in a portable format.', icon: <Download className="h-4 w-4" /> },
  { type: 'correct', label: 'Correct my data', desc: 'Ask us to fix inaccurate or incomplete data.', icon: <Pencil className="h-4 w-4" /> },
  { type: 'restrict', label: 'Restrict processing', desc: 'Ask us to limit how your data is used.', icon: <Ban className="h-4 w-4" /> },
  { type: 'delete', label: 'Delete my data', desc: 'Request erasure of your personal data.', icon: <Trash2 className="h-4 w-4" /> },
];

const requestLabel = (t: string) => DATA_RIGHTS.find((d) => d.type === t)?.label || t;

const statusStyle: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  in_review: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  approved: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

export const ConsentCenterPage: React.FC = () => {
  const [consents, setConsents] = useState<ConsentState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedKind, setSavedKind] = useState<string | null>(null);

  // Data-subject requests (DSAR) state.
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [pendingType, setPendingType] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Trust Center (marketplace M1): partner data-sharing grants + access log.
  const [grants, setGrants] = useState<DataSharingGrant[]>([]);
  const [accessLog, setAccessLog] = useState<DataAccessEntry[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  // Marketplace campaign nudges (design §13): opt-out lives in credit-service;
  // null = unknown (endpoint unreachable / module off) → hide the toggle.
  const [campaignOffers, setCampaignOffers] = useState<boolean | null>(null);
  const [savingOffers, setSavingOffers] = useState(false);

  const loadTrust = () => {
    getDataSharingGrants().then(setGrants).catch(() => {});
    getMyDataAccess().then(setAccessLog).catch(() => {});
    creditApi.marketingPrefs().then((p) => setCampaignOffers(p.campaign_offers)).catch(() => setCampaignOffers(null));
  };

  const toggleCampaignOffers = async (v: boolean) => {
    setSavingOffers(true);
    setCampaignOffers(v);
    try {
      await creditApi.setMarketingPrefs(v);
    } catch (e) {
      setCampaignOffers(!v);
      setError(getApiErrorMessage(e, 'Could not save your preference. Please try again.'));
    } finally {
      setSavingOffers(false);
    }
  };

  const loadRequests = () => {
    getMyPrivacyRequests()
      .then(setRequests)
      .catch(() => {
        /* non-blocking */
      });
  };

  useEffect(() => {
    let active = true;
    getConsents()
      .then((c) => active && setConsents(c))
      .catch(() => active && setError('Could not load your privacy preferences.'))
      .finally(() => active && setLoading(false));
    loadRequests();
    loadTrust();
    return () => {
      active = false;
    };
  }, []);

  const submitRequest = async () => {
    if (!pendingType) return;
    setSubmitting(true);
    try {
      await submitPrivacyRequest(pendingType, note.trim() || undefined);
      setPendingType(null);
      setNote('');
      loadRequests();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not submit your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (kind: string, granted: boolean) => {
    // Optimistic update; revert on failure.
    const prev = consents;
    setConsents((cs) => cs.map((c) => (c.kind === kind ? { ...c, granted, explicit: true } : c)));
    setSaving(kind);
    setError(null);
    try {
      const fresh = await updateConsents([{ kind, granted }]);
      setConsents(fresh);
      setSavedKind(kind);
      setTimeout(() => setSavedKind((k) => (k === kind ? null : k)), 2000);
    } catch (e: any) {
      setConsents(prev);
      setError(getApiErrorMessage(e, 'Could not save your preference. Please try again.'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
          <ShieldCheck className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">Privacy &amp; Trust Center</h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Choose how your data is used, see which partners you share with, and who accessed your data.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {consents.map((c) => {
            const when = fmtWhen(c.updated_at);
            return (
              <div key={c.kind} className="flex items-start gap-4 p-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">{c.label}</span>
                    {c.essential && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <Lock className="w-3 h-3" /> Required
                      </span>
                    )}
                    {savedKind === c.kind && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-600">
                        <Check className="w-3 h-3" /> Saved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{c.description}</p>
                  {c.explicit && when && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {c.granted ? 'Granted' : 'Declined'} on {when}
                    </p>
                  )}
                </div>
                <div className="pt-0.5">
                  <Switch
                    checked={c.granted}
                    disabled={c.essential || saving === c.kind}
                    onCheckedChange={(v) => toggle(c.kind, v)}
                    aria-label={c.label}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {campaignOffers !== null && (
        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-foreground">Product offer notifications</span>
              <p className="text-sm text-muted-foreground mt-0.5">
                Occasional sponsored offers from marketplace partners (at most 2 per month, always
                labelled). Turning this off stops campaign notifications entirely — it never affects
                your applications or products.
              </p>
            </div>
            <div className="pt-0.5">
              <Switch
                checked={campaignOffers}
                disabled={savingOffers}
                onCheckedChange={toggleCampaignOffers}
                aria-label="Product offer notifications"
              />
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Withdrawing a consent stops the related processing going forward; it does not
        delete data already collected. Use "Your data rights" below for access or deletion.
      </p>

      {/* Data-subject rights (DSAR) */}
      <div>
        <h2 className="text-base font-semibold text-foreground">Connected partners</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Financial partners you agreed to share data with when applying for their products.
        </p>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {grants.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No partner connections yet. When you apply for a partner product, your consent appears here.
            </p>
          ) : (
            grants.map((g) => {
              const withdrawn = !!g.withdrawn_at;
              return (
                <div key={g.id} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {g.purpose.replace(/_/g, ' ')}
                      {withdrawn && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">Withdrawn</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Shared: {(g.scopes || []).join(', ') || '—'} · granted {fmtWhen(g.granted_at)} · consent v{g.version}
                    </div>
                  </div>
                  {!withdrawn && (
                    <button
                      disabled={revoking === g.id}
                      onClick={async () => {
                        setRevoking(g.id);
                        try {
                          await withdrawDataSharingGrant(g.id);
                          loadTrust();
                        } catch {
                          /* toast handled globally? keep quiet, refresh anyway */
                          loadTrust();
                        } finally {
                          setRevoking(null);
                        }
                      }}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                    >
                      {revoking === g.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </div>
              );
            })
          )}
          {grants.length > 0 && (
            <p className="p-4 text-xs text-muted-foreground">
              Revoking stops future data sharing with that partner. Products you already hold continue to be
              serviced until settled.
            </p>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-base font-semibold text-foreground">Who accessed my data</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Every time a partner views your shared information, it is recorded here.
        </p>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {accessLog.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No partner has accessed your data.</p>
          ) : (
            accessLog.slice(0, 20).map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">
                    {e.accessor_name || 'A partner'}{' '}
                    <span className="font-normal text-muted-foreground">viewed your shared data</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.purpose.replace(/_/g, ' ')}{e.context ? ` · ${e.context}` : ''} · {fmtWhen(e.occurred_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-base font-semibold text-foreground">Your data rights</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Make a request about your personal data. We'll review it and keep you updated.
        </p>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {DATA_RIGHTS.map((d) => (
            <div key={d.type}>
              <div className="flex items-center gap-3 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  {d.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{d.label}</div>
                  <div className="text-xs text-muted-foreground">{d.desc}</div>
                </div>
                <button
                  onClick={() => {
                    setPendingType(pendingType === d.type ? null : d.type);
                    setNote('');
                  }}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Request
                </button>
              </div>
              {pendingType === d.type && (
                <div className="bg-muted/40 px-4 pb-4 pt-1">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add an optional note (e.g. which data, or why)…"
                    rows={2}
                    className="w-full rounded-lg border border-border bg-card p-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={submitRequest}
                      disabled={submitting}
                      className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
                    >
                      {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Submit request
                    </button>
                    <button
                      onClick={() => setPendingType(null)}
                      className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {requests.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Your requests</h3>
            <div className="divide-y divide-border">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">{requestLabel(r.request_type)}</div>
                    <div className="text-xs text-muted-foreground">Raised {fmtWhen(r.created_at)}</div>
                  </div>
                  {r.status === 'completed' && EXPORTABLE.has(r.request_type) && (
                    <button
                      onClick={() => downloadMyExport(r.id).catch((e) => setError(getApiErrorMessage(e, 'Could not download your export.')))}
                      className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                  )}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      statusStyle[r.status] || 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {r.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConsentCenterPage;
